import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useToast } from '@naf-simba/elements-toast';
import type { SnackbarKey } from 'notistack';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { actions as documentActions } from '@redux/modules/documents';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import useSelector from '@redux/typedHooks';
import { useGTMDataLayer } from './GTM/useGTMDataLayer';
import { useLoginRedirect } from './useLoginRedirect';

interface ReturnType {
  isFavorite: boolean;
  isToggling: boolean;
  toggleFavorite: () => void;
}

// Module-scoped (not per-card) so toggling favorites on different cards in the same grid still
// replaces the previous toast instead of stacking a new one for every click.
let lastFavoriteToastKey: SnackbarKey | undefined;

export const useToggleFavoriteBenefit = (
  benefitId?: string,
  slug?: string,
  initialIsFavorite = false,
  onToggled?: (isFavorite: boolean) => void,
  benefitTitle?: string,
  benefitPosition?: number,
): ReturnType => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth0();
  const { enqueueToast, closeToast } = useToast();
  const { redirectToLoginPage } = useLoginRedirect();
  const { pathname } = useLocation();
  const dataLayer = useGTMDataLayer();
  const hasMembership = useSelector(
    (state) => state.myMembership.customerInformation.data?.membership?.isValid ?? false,
  );
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isToggling, setIsToggling] = useState(false);

  // Re-sync when the fetched favorite status arrives after this benefit has already mounted.
  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [benefitId, initialIsFavorite]);

  // Keep the cached document (read again on back/forward navigation) in sync with the toggle.
  const updateFavorite = useCallback(
    (nextIsFavorite: boolean) => {
      setIsFavorite(nextIsFavorite);
      if (slug) {
        dispatch(documentActions.setDocumentFavorite({ slug, isFavorite: nextIsFavorite }));
      }
    },
    [dispatch, slug],
  );

  // Replaces whatever favorite-toggle toast is currently showing instead of piling up a new one.
  const showFavoriteToast = useCallback(
    (message: string, options?: Parameters<typeof enqueueToast>[1]) => {
      if (lastFavoriteToastKey !== undefined) {
        closeToast(lastFavoriteToastKey);
      }
      lastFavoriteToastKey = enqueueToast(message, options);
      return lastFavoriteToastKey;
    },
    [enqueueToast, closeToast],
  );

  const toggleFavorite = useCallback(async () => {
    if (!benefitId || isToggling) return;

    if (!isAuthenticated) {
      const toastKey = showFavoriteToast('Du må logge inn for å legge til favoritter.', {
        showIcon: true,
        variant: 'info',
        link: {
          text: 'Logg inn',
          onClick: () => {
            closeToast(toastKey);
            redirectToLoginPage(pathname);
          },
        },
      });
      return;
    }

    const nextIsFavorite = !isFavorite;
    setIsToggling(true);
    updateFavorite(nextIsFavorite);

    try {
      const url = ApiPaths[ApiType.apimNafNoApi].benefits.favorite(benefitId);
      if (nextIsFavorite) {
        await apiClient.put(ApiType.apimNafNoApi, { url });
      } else {
        await apiClient.delete(ApiType.apimNafNoApi, { url });
      }
      showFavoriteToast(nextIsFavorite ? 'Lagt til i dine favoritter' : 'Fjernet fra dine favoritter', {
        variant: 'success',
        showIcon: true,
      });
      dataLayer?.push({
        event: nextIsFavorite ? NAFGTMEvent.addFavoriteBenefit : NAFGTMEvent.removeFavoriteBenefit,
        benefit_id: benefitId,
        benefit_title: benefitTitle,
        benefit_position: benefitPosition,
        hasMembership,
        page: pathname,
      });
      onToggled?.(nextIsFavorite);
    } catch {
      // Revert the optimistic update if the request failed.
      updateFavorite(!nextIsFavorite);
      showFavoriteToast('Noe gikk galt. Prøv igjen senere.', {
        variant: 'error',
        showIcon: true,
      });
    } finally {
      setIsToggling(false);
    }
  }, [
    benefitId,
    isAuthenticated,
    isFavorite,
    isToggling,
    updateFavorite,
    showFavoriteToast,
    closeToast,
    redirectToLoginPage,
    pathname,
    dataLayer,
    benefitTitle,
    benefitPosition,
    hasMembership,
    onToggled,
  ]);

  return { isFavorite, isToggling, toggleFavorite };
};
